/**
 * When in a day, and what that does to the day's shape.
 *
 * Two rules, and the engine needs both: the **order** the stops are visited in, and the **earliest** each
 * one can start. A slot that only labelled a stop would be decoration — the defect this project keeps
 * finding — so `tối` has to mean the evening in the schedule, not just in a chip.
 *
 * The values are plain strings here rather than the contract's enum, matching how this package treats
 * `categories`: the engine compares and ranks, and knowing the closed set is the API's job. An unknown
 * string ranks as flexible rather than throwing, so a value added to the contract later degrades to "no
 * opinion" instead of failing a whole plan.
 */

/**
 * The order a day's stops are visited in.
 *
 * `cả ngày` and "not said" share the middle rank, and that is the load-bearing choice: **a stable sort by
 * rank leaves a day with no slots exactly as the user arranged it**, because every stop ranks equally. A
 * day where the user did say something keeps the unsaid ones in the middle of it — which is where a stop
 * with no time really belongs, rather than pushed to one end as though they had said "morning".
 */
const SLOT_RANK: Record<string, number> = {
  "sáng": 0,
  "trưa": 1,
  "cả ngày": 2,
  "chiều": 3,
  "tối": 4,
};

const FLEXIBLE_RANK = SLOT_RANK["cả ngày"]!;

/**
 * The earliest a stop in this slot can start, minutes from midnight.
 *
 * Floors rather than fixed times, for the reason the check-in time is one: a plan says when a stop *can*
 * begin, and a stop whose slot is `tối` at 14:00 would contradict the label the user chose. `sáng` has no
 * floor because the day's own start is already earlier than any morning.
 */
const SLOT_EARLIEST_MIN: Record<string, number> = {
  "trưa": 11 * 60,
  "chiều": 13 * 60 + 30,
  "tối": 17 * 60 + 30,
};

export function slotRank(slot: string | null | undefined): number {
  if (slot === null || slot === undefined) return FLEXIBLE_RANK;
  return SLOT_RANK[slot] ?? FLEXIBLE_RANK;
}

export function slotEarliestMin(slot: string | null | undefined): number | null {
  if (slot === null || slot === undefined) return null;
  return SLOT_EARLIEST_MIN[slot] ?? null;
}

/**
 * The day's stops in slot order, keeping the user's own order inside each slot.
 *
 * `Array.prototype.sort` is stable in every engine this runs on (ES2019 guarantees it), which is what makes
 * the "nothing said means nothing changes" property hold. A hand-rolled sort would have to reimplement
 * that guarantee to keep it.
 */
export function orderBySlot<T>(entries: ReadonlyArray<T>, slotOf: (entry: T) => string | null | undefined): T[] {
  return [...entries]
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => slotRank(slotOf(a.entry)) - slotRank(slotOf(b.entry)) || a.index - b.index)
    .map((wrapped) => wrapped.entry);
}

/**
 * Which of the day's slots suit a place, given what the place says about itself.
 *
 * The two vocabularies are different sizes — a place answers `sáng`, `trưa`, `chiều-tối` or `cả ngày`; a day
 * is planned as `sáng`, `trưa`, `chiều`, `tối` or `cả ngày` — so this is a mapping rather than a filter.
 * `chiều-tối` is the only one that splits, and it is the reason the vocabularies cannot be one.
 *
 * Two answers open everything, and for different reasons. A place with **nothing recorded** has not
 * restricted anything: absence is "not written down", never "nothing suits", which is the rule this schema
 * applies to every optional field. A place that is **`cả ngày`** has answered, and answered "any time".
 *
 * An unrecognised value is ignored rather than returned: it cannot be one of the five, so offering it would
 * put a chip on screen that the contract's own enum would then reject.
 */
export function slotsSuitedBy(bestTime: readonly string[]): string[] {
  if (bestTime.length === 0 || bestTime.includes("cả ngày")) return [...TRIP_SLOT_ORDER];

  const suited = new Set<string>();
  for (const answer of bestTime) {
    if (answer === "chiều-tối") {
      suited.add("chiều");
      suited.add("tối");
      continue;
    }
    if (TRIP_SLOT_ORDER.includes(answer)) suited.add(answer);
  }
  // Nothing usable was recorded — treat it as no answer rather than as no options, or the row would render a
  // place the user cannot schedule at all.
  if (suited.size === 0) return [...TRIP_SLOT_ORDER];
  return TRIP_SLOT_ORDER.filter((slot) => suited.has(slot));
}

/** The five, in the order a day runs — which is also the order they are offered in. */
export const TRIP_SLOT_ORDER: readonly string[] = ["sáng", "trưa", "chiều", "tối", "cả ngày"];
